import React, { useState } from "react";
import { message } from "antd";
import { useQuery } from "@apollo/client";
import { GET_ALL_ADMINS } from "../../api/graphql/users";
import CustomSelect from "../CustomSelect";
import styles from "./styles.module.scss";

export type Manager = {
  id: number;
  name?: string | null;
  email?: string | null;
};

export const managerName = (manager?: Manager | null) =>
  manager ? manager.name || manager.email || `#${manager.id}` : "";

// The option that takes the manager off; ids are numbers, so it can't clash.
const NO_MANAGER = "none";

interface ManagerSelectProps {
  /** The manager set on this record; null when there is none. */
  value: Manager | null;
  /** Shown greyed out while value is empty, e.g. the client's manager on an order. */
  inherited?: Manager | null;
  /** Saves the choice (null takes the manager off); a rejection keeps the old one. */
  onChange: (manager: Manager | null) => Promise<unknown>;
}

/** Picks a manager among the admins; only super admins are given it. */
const ManagerSelect: React.FC<ManagerSelectProps> = ({
  value,
  inherited,
  onChange,
}) => {
  // Every row has its own select: one request feeds them all from the cache.
  const { data } = useQuery<{ getAllAdmins: Manager[] }>(GET_ALL_ADMINS, {
    fetchPolicy: "cache-first",
  });
  // The choice being saved; undefined when nothing is in flight.
  const [pending, setPending] = useState<Manager | null | undefined>();

  const admins = data?.getAllAdmins ?? [];
  const shown = pending === undefined ? value : pending;
  const options = admins.map((admin) => ({
    value: String(admin.id),
    label: managerName(admin),
  }));
  // A manager who is no longer an admin still shows by name, not by id.
  if (shown && !admins.some((admin) => admin.id === shown.id)) {
    options.push({ value: String(shown.id), label: managerName(shown) });
  }
  if (shown) {
    options.unshift({
      value: NO_MANAGER,
      label: inherited ? "The client's manager" : "No manager",
    });
  }

  const handleChange = async (id: string) => {
    if (shown && id === String(shown.id)) return;
    const manager =
      id === NO_MANAGER
        ? null
        : (admins.find((admin) => String(admin.id) === id) ?? null);
    setPending(manager);
    try {
      await onChange(manager);
    } catch {
      message.error("The manager was not saved");
    } finally {
      setPending(undefined);
    }
  };

  return (
    <div title={!shown && inherited ? "The client's manager" : undefined}>
      <CustomSelect
        className={styles.select}
        value={shown ? String(shown.id) : ""}
        options={options}
        placeholder={inherited ? managerName(inherited) : "No manager"}
        disabled={pending !== undefined}
        aria-label="Manager"
        onChange={handleChange}
      />
    </div>
  );
};

export default ManagerSelect;
